import { AlertTriangleIcon } from '@langchain/untitled-ui-icons';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import Typography from '@mui/joy/Typography';

import { useGenericMultiDeleteMutation } from '@/hooks/useSwr';

interface DeleteProps {
  endpoint: string;
  invalidationPrefixes?: string[];
  items: { id: string; name: string }[];
  idParamName: string;
}

interface MultiDeleteModalProps extends DeleteProps {
  isOpen: boolean;
  modalText?: string;
  doClose: (didSucceed?: boolean) => void;
}

export function MultiDeleteModal({
  endpoint,
  invalidationPrefixes,
  items,
  idParamName,
  isOpen,
  modalText,
  doClose,
}: MultiDeleteModalProps) {
  const ids = items.map((item) => item.id);
  const deleteMutation = useGenericMultiDeleteMutation(
    {
      endpoint,
      idParamName,
      invalidationPrefixes,
      ids,
    },
    { onSuccess: () => doClose(true) }
  );

  return (
    <Modal open={isOpen} onClose={() => doClose()}>
      <ModalDialog
        variant="outlined"
        role="alertdialog"
        aria-labelledby="alert-dialog-modal-title"
        aria-describedby="alert-dialog-modal-description"
      >
        <Typography
          id="alert-dialog-modal-title"
          component="h2"
          startDecorator={<AlertTriangleIcon className="size-4" />}
        >
          Confirmation
        </Typography>
        <Divider />
        <Typography
          id="alert-dialog-modal-description"
          textColor="text.tertiary"
        >
          {modalText ?? (
            <>
              Are you sure you want to delete{' '}
              {items.length > 10 ? (
                items.length + ' items'
              ) : (
                <span className="font-semibold">
                  {items.map((item) => item.name).join(', ')}
                </span>
              )}
              {`?`}
            </>
          )}
        </Typography>

        {deleteMutation.error && (
          <Typography color="danger" fontSize="sm" sx={{ mt: 1 }}>
            {deleteMutation.error.message}
          </Typography>
        )}

        <Box
          sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}
        >
          <Button
            variant="soft"
            color="neutral"
            onClick={(e) => {
              e.stopPropagation();
              doClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="danger"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              deleteMutation.trigger();
            }}
            loading={deleteMutation.isMutating}
          >
            Delete
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
